import React, { useState } from 'react';
import styles from './InfluencerPlanGroupBar.module.css';
import cn from 'classnames';
import Toggle from 'ui/DataEntry/Toggle/Toggle';
import Modal, { ModalContent, ModalFooter, ModalTitle } from 'ui/General/Modal/Modal';
import Button from 'ui/Buttons/Button/Button';
import { PatchInfluencerPostGroupResponse, PatchYoutubeInfluencerPostGroupResponse } from '@round/api';
import { useQueryClient } from '@tanstack/react-query';
import { creatorbaseQueries } from '@round/api';
import Tooltip from '../../../../../ui/DataDisplay/Tooltip/Tooltip';
import { ReactComponent as InfoCircleIcon } from '../../../../../assets/InfoCircle.svg';

type Props = {
    isShared: boolean;
    onChange: (
        isShared: boolean
    ) => Promise<PatchInfluencerPostGroupResponse | PatchYoutubeInfluencerPostGroupResponse | void>;
    shouldDisable?: boolean;
    warningMessage?: string;
    unsharedText?: string;
};

const ShareWithCreatorbaseToggle = ({ isShared, onChange, shouldDisable, warningMessage, unsharedText }: Props) => {
    const queryClient = useQueryClient();

    const [isUpdateLoading, setIsUpdateLoading] = useState(false);
    const isSharedOptimistic = isUpdateLoading ? !isShared : isShared;
    const [isHiddenWarningRef, setIsHiddenWarningRef] = useState<SVGSVGElement | null>(null);

    const handleToggleChange = async (isShared: boolean) => {
        setIsUpdateLoading(true);
        const response = await onChange(isShared);
        if (response && response.status === 200) {
            queryClient.invalidateQueries({
                queryKey: creatorbaseQueries.campaigns.keys.lists(),
                type: 'all',
                refetchType: 'active',
            });
            queryClient.invalidateQueries({
                queryKey: creatorbaseQueries.campaigns.keys.infiniteLists(),
                type: 'all',
                refetchType: 'active',
            });

            queryClient.invalidateQueries({
                queryKey: creatorbaseQueries.postPaymentDetails.keys.lists(),
                type: 'all',
                refetchType: 'active',
            });
            queryClient.invalidateQueries({
                queryKey: creatorbaseQueries.posts.keys.lists(),
                type: 'all',
                refetchType: 'active',
            });
        }

        setIsUpdateLoading(false);
    };

    const [isConfirmUnshareModalOpen, setIsConfirmUnshareModalOpen] = useState(false);

    return (
        <>
            <div className={cn(styles.groupInfoColumn, styles.creatorbaseToggle)}>
                <div className={styles.row}>
                    <Toggle
                        value={isSharedOptimistic}
                        disabled={shouldDisable || isUpdateLoading}
                        onChange={(isShared) => {
                            if (!isShared) {
                                setIsConfirmUnshareModalOpen(true);
                                return;
                            }

                            handleToggleChange(isShared);
                        }}
                        className={cn(styles.hiddenToggle, {
                            [styles.hiddenToggleOn]: isSharedOptimistic,
                            [styles.warningToggle]: !shouldDisable && warningMessage,
                        })}
                    />
                    {warningMessage && (
                        <>
                            <Tooltip anchorElement={isHiddenWarningRef} value={warningMessage} />
                            <InfoCircleIcon
                                ref={setIsHiddenWarningRef}
                                className={cn(styles.warningIcon, {
                                    [styles.warningIconRed]: !shouldDisable && warningMessage,
                                })}
                            />
                        </>
                    )}
                </div>

                <span className={cn(styles.groupInfoColumnValue, styles.hiddenToggleValue)}>
                    {isShared ? 'Live & Shared' : unsharedText || 'Unshared'}
                </span>
            </div>

            <Modal
                className={styles.alignCenter}
                closeOnOverlayClick
                isOpen={isConfirmUnshareModalOpen}
                onClose={() => setIsConfirmUnshareModalOpen(false)}
            >
                <ModalTitle>Remove campaign from creatorbase?</ModalTitle>
                <ModalContent>This might affect creatorbase users</ModalContent>
                <ModalFooter align="center">
                    <Button onClick={() => setIsConfirmUnshareModalOpen(false)}>Cancel</Button>
                    <Button
                        type="filled"
                        color="black"
                        onClick={() => {
                            handleToggleChange(!isShared);
                            setIsConfirmUnshareModalOpen(false);
                        }}
                    >
                        Remove campaign
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ShareWithCreatorbaseToggle;
